import React from 'react';

import { Button, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';

import DynamicModal from '../../../components/DynamicModal';

type StepProps = {
  step: number;
  title: string;
  subtitle: string;
};

const Step = ({ step, title, subtitle }: StepProps): JSX.Element => {
  return (
    <Flex gap="2x">
      <Flex
        borderRadius="16px"
        background="tertiary"
        sx={{ py: '2px', px: '6px' }}
        height="fit-content"
      >
        <Text size="100">{step}</Text>
      </Flex>
      <Flex flexDirection="column" flex="1" gap="1x">
        <Text fontWeight="semibold">{title}</Text>
        <Text size="100" textColor="tertiary">
          {subtitle}
        </Text>
      </Flex>
    </Flex>
  );
};

const STEPS: StepProps[] = [
  {
    step: 1,
    title: 'Find a quiet room with good Wi-Fi',
    subtitle: 'A distraction free environment helps you and your dog focus on training.',
  },
  {
    step: 2,
    title: 'Position your phone or computer',
    subtitle:
      'You want to have your hands available to learn and train, so make sure to prop up your phone or use a laptop so your trainer can see you and your dog in the training session.',
  },
  {
    step: 3,
    title: 'Open the video call link sent by your trainer',
    subtitle:
      'Your trainer will arrange a video call that they will send a link for through your conversation.',
  },
];

export interface VirtualTrainingModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const VirtualTrainingModal = ({
  isOpen,
  setIsOpen,
}: VirtualTrainingModalProps): JSX.Element => {
  return (
    <DynamicModal
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      desktopSX={{ paddingBottom: '0px' }}
      isFullHeight
    >
      <Flex
        width="100%"
        height="100%"
        flexDirection="column"
        justifyContent={['space-between', 'flex-start']}
        data-testid="virtual-training-modal"
      >
        <Flex flexDirection="column" gap="4x" paddingBottom={['3x', '8x']}>
          <Flex flexDirection="column">
            <Heading
              py="3x"
              margin="0x"
              sx={{
                fontSize: ['16px !important', '20px !important'],
                lineHeight: ['150% !important', '125% !important'],
              }}
            >
              What do I need for virtual training?
            </Heading>
            <Paragraph pb="3x" pt={['4x', '0x']} margin="0x">
              Train in your own space, with greater scheduling flexibility and access to trainers
              from all over.
            </Paragraph>
            <Paragraph margin="0x">Here's what you need to have for virtually training:</Paragraph>
          </Flex>
          <Flex py="2x" gap="4x" flexDirection="column">
            {STEPS.map((stepProps) => (
              <Step key={stepProps.step} {...stepProps} />
            ))}
          </Flex>
        </Flex>
        <Button
          variant="default"
          onClick={() => setIsOpen(false)}
          fullWidth
          ml={['0x', '1x']}
          mb={['0x', '1x']}
          py="2x"
          px="4x"
        >
          Close
        </Button>
      </Flex>
    </DynamicModal>
  );
};
